import {
  getClientRectWithMargin,
  reduceTransformBoundingBoxes,
} from "./bounding-box";

import type { Ref } from "lit/directives/ref.js";
import type { SlideScrollAnchorPoint, SlideTransformState } from "../types.d";

const ZOOM_ANCHOR_TOP_PADDING = 2;

export interface ZoomControllerProps {
  slideRef: Ref<HTMLElement>;
  minWindowHeight: number;
  maxWindowHeight: number;
  windowWidth: number;
  verticalPadding?: number;
  horizontalPadding?: number;
  hideShadow?: boolean;
  defaultUnitScale?: number;
}

export class ZoomController {
  constructor({
    slideRef,
    minWindowHeight,
    maxWindowHeight,
    windowWidth,
    verticalPadding = 0,
    horizontalPadding = 0,
    defaultUnitScale = undefined,
  }: ZoomControllerProps) {
    this._slideRef = slideRef;
    this._verticalPadding = verticalPadding;
    this._horizontalPadding = horizontalPadding;
    this._maxWindowHeight = maxWindowHeight;
    this._minWindowHeight = minWindowHeight;
    this._windowWidth = windowWidth;
    this._defaultUnitScale = defaultUnitScale;
  }
  private _slideRef: Ref<HTMLElement>;
  private _initAnimation = false;
  private _contentEl?: HTMLElement;
  private _contentHeight = 540;
  private _contentWidth = 960;
  private _maxWindowHeight = 540;
  private _minWindowHeight = 540;
  private _windowWidth = 960;
  private _verticalPadding: number;
  private _horizontalPadding: number;
  private _scrollX = 0; // -0.5 => scrolled all the way to left, 0.5 => scrolled all the way to right
  private _scrollY = 0; // 0 => scrolled to top, 1 => scrolled to bottom
  private _scale = 1;
  private _defaultUnitScale?: number;
  set contentEl(content: HTMLElement | undefined) {
    this._contentEl = content;
  }
  get contentEl() {
    return this._contentEl;
  }
  set maxWindowHeight(height: number) {
    this._maxWindowHeight = height;
  }
  set minWindowHeight(height: number) {
    this._minWindowHeight = height;
  }
  set windowWidth(width: number) {
    this._windowWidth = width;
  }
  set contentHeight(height: number) {
    this._contentHeight = height;
  }
  set contentWidth(width: number) {
    this._contentWidth = width;
  }
  set defaultUnitScale(defaultUnitScale: number | undefined) {
    this._defaultUnitScale = defaultUnitScale;
  }
  get minScale(): number {
    return Math.min(
      (this._minWindowHeight - 2 * this._verticalPadding) / this._contentHeight,
      (this._windowWidth - 2 * this._horizontalPadding) / this._contentWidth,
    );
  }
  get unitScale(): number {
    if (this._defaultUnitScale) return this._defaultUnitScale;

    return (
      (this._windowWidth - 2 * this._horizontalPadding) / this._contentWidth
    );
  }
  get scaledContentHeight() {
    return this._scale * this._contentHeight;
  }
  get scaledContentWidth() {
    return this._scale * this._contentWidth;
  }
  get maxTranslateX(): number {
    return Math.max(
      0,
      this.scaledContentWidth -
        (this._windowWidth - 2 * this._horizontalPadding),
    );
  }
  get minTranslateY(): number {
    return Math.max(
      (this._minWindowHeight - this.scaledContentHeight) / 2,
      this._verticalPadding,
    );
  }
  get maxTranslateY(): number {
    if (
      this.scaledContentHeight + 2 * this._verticalPadding <
      this._minWindowHeight
    )
      return this.minTranslateY;
    if (this._scale <= this.minScale) return this.minTranslateY;
    return (
      -1 *
      Math.max(
        -1 * this.minTranslateY,
        this.scaledContentHeight - (this._maxWindowHeight - this.minTranslateY),
      )
    );
  }
  get translateX(): number {
    return -1 * this._scrollX * this.maxTranslateX;
  }
  get translateY(): number {
    return this.interpolate(
      this.minTranslateY,
      this.maxTranslateY,
      this._scrollY,
    );
  }
  get isScrolling(): boolean {
    return this.translateY < this.minTranslateY;
  }
  get isScrolledToBottom(): boolean {
    return this.translateY <= this.maxTranslateY;
  }
  private calculateScale(zoom: number) {
    return zoom < 0
      ? this.minScale
      : zoom < 1
      ? this.interpolate(this.minScale, this.unitScale, zoom)
      : zoom * this.unitScale;
  }
  private getScaleValue(
    userInputZoom: number,
    zoomFitElements: Array<string> | undefined,
    zoomFillElements: Array<string> | undefined,
  ) {
    const scale = this.calculateScale(userInputZoom);
    if (!this._contentEl) return scale;
    if (!zoomFillElements?.length && !zoomFitElements?.length) return scale;
    const zoomElementIds = zoomFillElements ?? zoomFitElements ?? [];
    const zoomElements: Array<HTMLElement> = zoomElementIds
      .map((id) => this._contentEl?.querySelector(`[data-id="${id}"]`))
      .filter((element) => !!element)
      .map((el) => el as HTMLElement);
    if (!zoomElements?.length) return scale;
    const { width: zoomWidth, height: zoomHeight } =
      reduceTransformBoundingBoxes(zoomElements.map(getClientRectWithMargin));
    const clientScale =
      this._contentEl?.offsetWidth /
      this._contentEl?.getBoundingClientRect().width;
    const unitScale = Math.min(
      (this._windowWidth - 2 * this._horizontalPadding) /
        ((zoomWidth || 1) * clientScale),
      (this._maxWindowHeight - 2 * this._verticalPadding) /
        ((zoomHeight || 1) * clientScale),
    );
    return zoomFillElements?.length ? unitScale : Math.min(unitScale, scale);
  }
  private getScrollValue(
    userInputScrollX: number,
    userInputScrollY: number,
    scrollPoints: Array<string> | undefined,
    scrollAnchorPoint: SlideScrollAnchorPoint | undefined,
  ): { scrollX: number | undefined; scrollY: number | undefined } {
    let scrollX = userInputScrollX;
    let scrollY = userInputScrollY;
    if (!this._contentEl || !scrollPoints?.length) return { scrollX, scrollY };
    const scrollElements = scrollPoints
      .map(
        (scrollPoint) =>
          this._contentEl?.querySelector(`[data-id="${scrollPoint}"]`),
      )
      .filter((el) => !!el)
      .map((el) => el as HTMLElement);
    if (!scrollElements?.length) return { scrollX, scrollY };
    const {
      left: contentClientLeft,
      top: contentClientTop,
      width: contentClientWidth,
    } = this._contentEl.getBoundingClientRect();
    const {
      left: scrollLeft,
      midX: scrollMidX,
      right: scrollRight,
      top: scrollTop,
      midY: scrollMidY,
      bottom: scrollBottom,
    } = reduceTransformBoundingBoxes(
      scrollElements.map(getClientRectWithMargin),
    );
    const clientScale =
      (this._scale * this._contentEl.offsetWidth) / contentClientWidth;
    let scrollHorizontalPosition, horizontalDestination;
    switch (scrollAnchorPoint) {
      case "left":
      case "bottom-left":
      case "top-left":
        scrollHorizontalPosition =
          (scrollLeft - contentClientLeft) * clientScale;
        horizontalDestination =
          (contentClientWidth * clientScale) / 2 -
          this._windowWidth / 2 +
          this._horizontalPadding;
        break;
      case "right":
      case "bottom-right":
      case "top-right":
        scrollHorizontalPosition =
          (scrollRight - contentClientLeft) * clientScale;
        horizontalDestination =
          (contentClientWidth * clientScale) / 2 +
          this._windowWidth / 2 -
          this._horizontalPadding;
        break;
      case "center":
      case "bottom":
      case "top":
      default:
        scrollHorizontalPosition =
          (scrollMidX - contentClientLeft) * clientScale;
        horizontalDestination = (contentClientWidth * clientScale) / 2;
        break;
    }
    scrollX = 0;
    if (this.maxTranslateX > 0) {
      scrollX = Math.min(
        0.5,
        Math.max(
          -0.5,
          (scrollHorizontalPosition - horizontalDestination) /
            this.maxTranslateX,
        ),
      );
    }
    let scrollVerticalPosition, verticalDestination;
    switch (scrollAnchorPoint) {
      case "top":
      case "top-left":
      case "top-right":
        scrollVerticalPosition = (scrollTop - contentClientTop) * clientScale;
        verticalDestination = ZOOM_ANCHOR_TOP_PADDING - this._verticalPadding;
        break;
      case "bottom":
      case "bottom-left":
      case "bottom-right":
        scrollVerticalPosition =
          (scrollBottom - contentClientTop) * clientScale;
        verticalDestination = this._maxWindowHeight - 2 * this._verticalPadding;
        break;
      case "center":
      case "left":
      case "right":
      default:
        scrollVerticalPosition = (scrollMidY - contentClientTop) * clientScale;
        verticalDestination = this._maxWindowHeight / 2 - this._verticalPadding;
        break;
    }
    scrollY = 0;
    if (Math.abs(this.maxTranslateY - this.minTranslateY) > 0) {
      scrollY = Math.min(
        1,
        Math.max(
          0,
          (scrollVerticalPosition - verticalDestination) /
            Math.abs(this.maxTranslateY - this.minTranslateY),
        ),
      );
    }
    return { scrollX, scrollY };
  }
  update({
    zoom = 0,
    zoomFillElements = undefined,
    zoomFitElements = undefined,
    scrollX = 0,
    scrollY = 0,
    scrollPoints = undefined,
    scrollAnchorPoint = undefined,
    minimizeTitle = undefined,
  }: SlideTransformState) {
    setTimeout(() => (this._initAnimation = true), 50);
    this._scale = this.getScaleValue(zoom, zoomFitElements, zoomFillElements);
    const { scrollX: calcScrollX, scrollY: calcScrollY } = this.getScrollValue(
      scrollX,
      scrollY,
      scrollPoints,
      scrollAnchorPoint,
    );
    this._scrollX = calcScrollX ?? 0;
    this._scrollY = calcScrollY ?? 0;
    if (this._slideRef.value) {
      const style = `
        ${
          this._initAnimation
            ? ""
            : "--scroll-transition: 0; --title-transition: 0;"
        }
        --content-translate-x: ${this.translateX}px;
        --content-translate-y: ${this.translateY}px;
        --content-scale: ${this._scale};
        --scrim-opacity: ${this.isScrolledToBottom ? 0 : 1};
      `;
      this._slideRef.value.setAttribute("style", style);
      if (this.isScrolling || minimizeTitle) {
        this._slideRef.value.classList.add("title-minimized");
      } else {
        this._slideRef.value.classList.remove("title-minimized");
      }
    }
  }
  private interpolate(x: number, y: number, amt: number) {
    return x * (1 - amt) + amt * y;
  }
}
